import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Form as FinalForm } from 'react-final-form';
import { pathByRouteName } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';
import { propTypes } from '../../util/types';
import {
  Form,
  FieldRichTextInput,
  SecondaryButton,
  PrimaryButton,
  Avatar,
  IconSpinner,
} from '../../components';
import { searchUsers, getSearchResults, getSearchInProgress } from '../../ducks/userSearch.duck';
import { userDisplayNameAsString } from '../../util/data';
import classNames from 'classnames';

import css from './CommunityCommentForm.module.css';

const CommunityCommentFormComponent = ({
  className = null,
  rootClassName = null,
  disabled,
  onCancel = null,
  intl,
  formId,
  addCommentError = null,
  addCommentInProgress = false,
  commentDisplayName,
  onSearchUsers,
  searchResults = undefined,
  searchInProgress = false,
  submitMessageTranslationId = null,
  ...props
}) => {
  const [richTextInputRef, setRichTextInputRef] = useState(null);
  const [mentionSuggestions, setMentionSuggestions] = useState(undefined);

  const formatMentionSuggestions = useCallback(users => {
    return users.map(user => {
      const displayName = userDisplayNameAsString(user).trim();
      return {
        key: user.id.uuid,
        text: (
          <span className={css.userSuggestion}>
            <Avatar
              className={css.userSuggestionAvatar}
              initialsClassName={css.userSuggestionAvatarInitials}
              user={user}
              disableProfileLink
            />
            {displayName}
          </span>
        ),
        value: displayName,
        link: pathByRouteName('ProfilePage', routeConfiguration(), { id: user.id.uuid }),
        url: pathByRouteName('ProfilePage', routeConfiguration(), { id: user.id.uuid }),
        user: user,
      };
    });
  }, []);

  const handleMentionSearch = useCallback(
    searchTerm => {
      if (!searchTerm?.trim()) {
        setMentionSuggestions(undefined);
        return;
      }

      // Use the new search API
      onSearchUsers(searchTerm, { limit: 10 });
    },
    [onSearchUsers]
  );

  useEffect(() => {
    if (searchResults?.length) {
      const formattedSuggestions = formatMentionSuggestions(searchResults);
      setMentionSuggestions(formattedSuggestions);
    } else if (searchInProgress) {
      // Show loading state
      setMentionSuggestions([
        {
          key: 'loading',
          text: (
            <span className={css.userSuggestion}>
              <IconSpinner className={css.loadingSpinner} />
              <FormattedMessage id="CommunityCommentForm.searching" />
            </span>
          ),
          value: '',
          disabled: true,
        },
      ]);
    } else {
      // Don't show dropdown when no results - set to undefined instead of empty array
      setMentionSuggestions(undefined);
    }
  }, [searchResults, searchInProgress, formatMentionSuggestions]);

  const hasContent = useCallback(values => {
    if (!values.content) return false;

    if (typeof values.content === 'string') {
      const trimmed = values.content.trim();
      if (trimmed.length === 0) return false;

      // Check if it's just empty HTML tags from Lexical
      const withoutTags = trimmed.replace(/<[^>]*>/g, '').replace(/\s/g, '');
      return withoutTags.length > 0;
    }

    if (values.content.getCurrentContent) {
      return values.content.getCurrentContent().hasText();
    }

    return false;
  }, []);

  return (
    <FinalForm
      {...props}
      render={fieldRenderProps => {
        const { form, values, handleSubmit } = fieldRenderProps;

        const commentPlaceholderMessage = intl.formatMessage({
          id: 'CommunityCommentForm.commentPlaceholder',
        });

        const postingAsDisplayName = intl.formatMessage(
          { id: 'CommunityCommentForm.commentAs' },
          { displayName: commentDisplayName }
        );

        const submitCommentMessage = intl.formatMessage({
          id: submitMessageTranslationId || 'CommunityCommentForm.postSubmit',
        });

        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress = addCommentInProgress;
        const submitDisabled = disabled || submitInProgress || !hasContent(values);

        const handleFormSubmit = async values => {
          try {
            const result = await handleSubmit(values);
            form.restart();
            richTextInputRef?.resetEditor?.();
            return result;
          } catch (error) {
            throw error;
          }
        };

        const handleCancel = e => {
          if (onCancel) {
            onCancel();
          } else {
            form.restart();
            richTextInputRef?.resetEditor?.();
            e.preventDefault();
          }
        };

        return (
          <Form className={classes} onSubmit={handleFormSubmit}>
            {commentDisplayName && <span className={css.commentAs}>{postingAsDisplayName}</span>}
            <div className={css.commentWrapper}>
              <FieldRichTextInput
                onRef={setRichTextInputRef}
                wrapperClassName={css.commentInputWrapper}
                editorClassName={css.commentInputEditor}
                type="textarea"
                id={formId ? `${formId}.content` : 'content'}
                name="content"
                placeholder={commentPlaceholderMessage}
                mention={{
                  separator: ' ',
                  trigger: '@',
                  suggestions: mentionSuggestions || [],
                  caseSensitive: false,
                  keepCursorAtEndOfWord: false,
                  optionClassName: css.userSuggestion,
                }}
                onMentionSearch={handleMentionSearch}
                hideToolbar
              />

              <div className={css.bottomWrapper}>
                <SecondaryButton className={css.resetButton} onClick={handleCancel}>
                  <FormattedMessage id="CommunityCommentForm.cancel" />
                </SecondaryButton>
                <PrimaryButton
                  className={css.submitButton}
                  type="submit"
                  inProgress={submitInProgress}
                  disabled={submitDisabled}
                  spinnerClassName={css.spinnerIcon}
                >
                  {submitCommentMessage}
                </PrimaryButton>
              </div>
            </div>
            {addCommentError && (
              <p className={css.error}>
                <FormattedMessage id="CommunityCommentForm.postError" />
              </p>
            )}
          </Form>
        );
      }}
    />
  );
};


const { bool, func, string, array, node } = PropTypes;

CommunityCommentFormComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  onCancel: func,
  addCommentInProgress: bool.isRequired,
  addCommentError: propTypes.error,
  searchResults: array,
  searchInProgress: bool,
  onSearchUsers: func.isRequired,
  commentDisplayName: node,
  formId: string,
  submitMessageTranslationId: string,
};

const mapStateToProps = state => {
  return {
    searchResults: getSearchResults(state),
    searchInProgress: getSearchInProgress(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onSearchUsers: (searchTerm, options) => dispatch(searchUsers(searchTerm, options)),
});

const CommunityCommentForm = compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(CommunityCommentFormComponent);

CommunityCommentForm.displayName = 'CommunityCommentForm';

export default CommunityCommentForm;
